import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../utils/toast";
import { getFloorWithRelations, mergeFloorCells } from "../../services/floorService";
import { getBuildingWithRelations } from "../../services/buildingService";
import { patchRoom, patchRoomLayout } from "../../services/roomService";
import { createTable, deleteTable } from "../../services/tableService";
import {
  buildingTypeOf,
  deskSeatSlots,
  getRoomTemplates,
  getSpaceTypes,
  roomSpaceTypes,
} from "../../services/spaceCatalogService";
import {
  canSave,
  cellKey,
  isLockedRole,
  lowestFloorOf,
  summariseFloor,
  validateFloor,
  WALKABLE as WALKABLE_ROLES,
} from "../../utils/floorLayoutValidation";
import {
  checkSeatCapacityTransition,
  MAX_SEATS_PER_CELL,
} from "../../utils/seatCapacity";
import { planTableReconciliation } from "../../utils/tableReconciliation";
import { inferSpaceTypeCodeFromSeatCount } from "../../utils/spaceTypeInference";
import { readRoomCell } from "../../utils/floorPlan";
import { packSeatsIntoTables } from "../../utils/tableTypes";
import "../../styles/floor-layout-designer.css";

/**
 * The design layout of ONE floor, for embedding in the Floor pages.
 *
 * Paint what each cell is, merge cells into a larger room, and say what each
 * room is used for. The plan drives the 3D twin, so a layout that would strand
 * the walking agent cannot be saved — the checks panel and the Save button are
 * both driven by floorLayoutValidation.js.
 *
 * Two things are deliberately NOT editable here:
 *  - the grid size, which belongs to the building; and
 *  - the lift and stair cores, which are placed once on the building and
 *    stamped onto every floor so they line up from storey to storey.
 *
 * @param {number} floorId
 * @param {"edit"|"view"} mode  view drops the palette, merging and Save, and
 *   keeps the plan and its checks so the layout can be read without risk.
 */
const ROLES = [
  { id: "EMPTY", name: "Room", key: "1", hint: "An occupied space: offices, meeting rooms, stores." },
  { id: "CORRIDOR", name: "Corridor", key: "2", hint: "Circulation people walk along." },
  { id: "LOBBY", name: "Lobby", key: "3", hint: "Open reception or atrium; also walkable." },
  {
    id: "ENTRANCE",
    name: "Entrance",
    key: "4",
    hint: "The way in from outside. Must sit on the building's outer edge, and only the lowest floor has one — it also tells the 3D view which way the building faces.",
  },
  { id: "VOID", name: "Void", key: "5", hint: "Open to the floor below; no floor here." },
];
const LOCKED_ROLES = [
  { id: "ELEVATOR", name: "Lift", hint: "Placed on the building, locked on every floor." },
  { id: "STAIRS", name: "Stairs", hint: "Placed on the building, locked on every floor." },
];
const ROLE_BY_ID = Object.fromEntries([...ROLES, ...LOCKED_ROLES].map((r) => [r.id, r]));
const roleName = (id) => (ROLE_BY_ID[id] ?? ROLE_BY_ID.EMPTY).name;

/** Turn one floor-with-relations payload into the shape the validator works on. */
function toFloorModel(dto) {
  const building = dto?.building ?? null;
  const rows = Number(dto?.rows || building?.rows) || 0;
  const columns = Number(dto?.columns || building?.columns) || 0;
  const cells = {};
  (dto?.rooms ?? []).forEach((room) => {
    // Geometry (type, spans, absorbedBy) comes from the shared plan reader so
    // this and the desk grid cannot drift into disagreeing about what a saved
    // merge looks like.
    const plan = readRoomCell(room);
    cells[cellKey(room.row, room.column)] = {
      ...plan,
      roomTemplateId: room.room_template_id ?? null,
      // The REAL tables this room already holds — not just a count. Table
      // reconciliation (labelling creates/removes desks) and the seat-cap
      // check both need each table's capacity and whether a job sits at it.
      tableCount: plan.tables.length,
      // Custom-labelled rooms (item 3) are never a silent write: this stays
      // false until the analyst explicitly accepts a suggestion, and Save is
      // what actually renames the room to say so.
      pendingCustom: false,
    };
  });
  return {
    id: dto?.floor_id,
    name: dto?.name || dto?.floor_code || "Floor",
    code: dto?.floor_code || "",
    level: dto?.level ?? null,
    buildingId: dto?.building_id ?? building?.building_id ?? null,
    building,
    rows,
    columns,
    cells,
  };
}

/** Sum of `table.capacity` over a room's REAL tables — the seat cap's unit. */
const realSeatsOf = (cell) => (cell?.tables ?? []).reduce((n, t) => n + (Number(t.capacity) || 0), 0);

/**
 * Which furniture layout a cell would draw, and why — the exact preference
 * order the server's /room-template/resolve uses (pin, exact, fits, default,
 * none). Standalone so both the inspector (for the selected cell) and the
 * save-time seat-cap check (for every dirty cell) resolve the same way.
 */
/**
 * Whether a furniture layout's footprint fits inside a cell.
 *
 * A 1×2 layout cannot be drawn in a 1×1 room — there is no second cell to put
 * the other half in — so a room has to be merged to that size first.
 */
function templateFitsCell(template, cell) {
  return (
    (Number(template?.row_span) || 1) <= (cell?.rowSpan ?? 1) &&
    (Number(template?.col_span) || 1) <= (cell?.colSpan ?? 1)
  );
}

function resolveTemplateForCell(cell, roomTemplates) {
  if (!cell || !cell.spaceTypeId) return { template: null, match: "none" };
  const candidates = roomTemplates
    .filter((t) => t.space_type_id === cell.spaceTypeId)
    .sort((a, b) => b.row_span - a.row_span || b.col_span - a.col_span);
  if (!candidates.length) return { template: null, match: "none" };
  const rowSpan = cell.rowSpan ?? 1;
  const colSpan = cell.colSpan ?? 1;
  const pinned = candidates.find((t) => t.room_template_id === cell.roomTemplateId);
  if (pinned) return { template: pinned, match: "pinned" };
  const exact = candidates.find((t) => t.row_span === rowSpan && t.col_span === colSpan);
  if (exact) return { template: exact, match: "exact" };
  const fits = candidates.find((t) => templateFitsCell(t, cell));
  if (fits) return { template: fits, match: "fits" };
  const fallback = candidates.find((t) => t.is_default);
  return fallback ? { template: fallback, match: "default" } : { template: null, match: "none" };
}

/** A room labelled from an inferred guess, not a human pick (item 3). */
const CUSTOM_TAG = " (Custom)";
const withCustomTag = (name) => (String(name || "").includes("(Custom)") ? name : `${name}${CUSTOM_TAG}`);
const isCustomLabelled = (name) => String(name || "").includes("(Custom)");

/**
 * Create or remove REAL `table` rows so a room's desks match its resolved
 * furniture layout — reconcile, never duplicate (see tableReconciliation.js
 * for the matching rule). Only desk-bearing items become tables; a meeting
 * or boardroom's chairs never do.
 */
async function reconcileRoomFurniture(cell, roomTemplates) {
  const resolved = resolveTemplateForCell(cell, roomTemplates);
  // The template's seats, packed into the one or two desks the desk grid can
  // draw — not one table per template item, which the grid would truncate.
  const targets = resolved.template
    ? packSeatsIntoTables(
        deskSeatSlots(resolved.template).reduce((n, seats) => n + seats, 0),
      )
    : [];
  const plan = planTableReconciliation(cell.tables ?? [], targets);
  let n = (cell.tables ?? []).length;
  for (const capacity of plan.toCreate) {
    n += 1;
    // eslint-disable-next-line no-await-in-loop
    await createTable({
      room_id: cell.roomId,
      tableCode: `${cell.roomCode || cell.roomId}-D${n}`,
      name: `Desk ${n}`,
      capacity,
      orientation: "HORIZONTAL",
    });
  }
  for (const tableId of plan.toDeleteIds) {
    // eslint-disable-next-line no-await-in-loop
    await deleteTable(tableId);
  }
}

/**
 * @param {(reason: "save"|"merge") => void} [onLayoutChange] Called after a
 *   change has been persisted and reloaded. The plan is shared state — the desk
 *   grid on the Floor page reads the same rooms — so a view that caches it
 *   needs to be told when cell types or spans have moved under it.
 *
 *   The reason matters on the Create page, which advances to the desk step only
 *   once the plan has been SAVED; a merge is a step within designing it, not a
 *   signal that the design is done.
 * @param {number} [desksVersion] Bump when a desk has been added or removed in
 *   the desk grid. This designer reads each room's tables for the seat cap and
 *   the census, so those counts go stale otherwise.
 */
export default function FloorLayoutDesigner({
  floorId,
  mode = "edit",
  onLayoutChange,
  desksVersion = 0,
}) {
  const readOnly = mode === "view";

  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [floor, setFloor] = useState(null);
  const [building, setBuilding] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [buildingCells, setBuildingCells] = useState([]);
  const [spaceTypes, setSpaceTypes] = useState([]);
  const [roomTemplates, setRoomTemplates] = useState([]);

  const [tool, setTool] = useState("EMPTY");
  const [merging, setMerging] = useState(false);
  const [marquee, setMarquee] = useState(null); // { from:[r,c], to:[r,c] }
  const [selected, setSelected] = useState(null); // [row, column]
  const [dirty, setDirty] = useState({}); // roomId -> true
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false); // a merge/split round-trip
  const [focusCells, setFocusCells] = useState([]);
  const painting = useRef(false);
  const history = useRef([]);

  // ------------------------------------------------------------ load
  const load = useCallback(async () => {
    if (!floorId) {
      setStatus("empty");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const dto = await getFloorWithRelations(floorId);
      const model = toFloorModel(dto);
      // The building, not just this floor's sibling list: the same payload
      // carries the storeys AND the building type, and the type is what
      // narrows the space picker to spaces this kind of building has. Asking
      // /building-type for the whole catalogue to translate one id into one
      // code was a second request for something already on the way here.
      const [tree, sts, tpls] = await Promise.all([
        model.buildingId ?
          getBuildingWithRelations(model.buildingId).catch(() => null)
        : null,
        getSpaceTypes().catch(() => []),
        getRoomTemplates().catch(() => []),
      ]);
      setFloor(model);
      setBuilding(tree);
      setBuildingCells(model.building?.building_cell ?? tree?.cells ?? []);
      setSiblings(
        (tree?.floors ?? []).map((f) => ({
          id: f.floor_id,
          name: f.name,
          code: f.floor_code,
          level: f.level ?? null,
        })),
      );
      setSpaceTypes(sts);
      setRoomTemplates(tpls);
      setDirty({});
      history.current = [];
      setStatus("ready");
    } catch (e) {
      setError(e?.message || "Could not load this floor's layout.");
      setStatus("error");
    }
  }, [floorId]);

  useEffect(() => {
    load();
  }, [load]);

  // Reload after a write, then tell the host the plan moved. Kept separate from
  // `load` so the initial mount does not report a change nobody made.
  const reloadAndNotify = useCallback(
    async (reason) => {
      await load();
      if (typeof onLayoutChange === "function") onLayoutChange(reason);
    },
    [load, onLayoutChange],
  );

  /**
   * Re-read only the rooms' tables, after a desk was added or removed below.
   *
   * Deliberately not a full `load()`: that resets `dirty` and the undo history,
   * which would throw away painting the analyst has not saved yet. Tables are
   * the one part of a cell this designer reads but never edits, so merging just
   * those cannot clobber an unsaved change.
   */
  const refreshTables = useCallback(async () => {
    if (!floorId) return;
    try {
      const dto = await getFloorWithRelations(floorId);
      const tablesByCell = new Map(
        (dto?.rooms ?? []).map((room) => [
          cellKey(room.row, room.column),
          room.tables ?? [],
        ]),
      );
      setFloor((current) => {
        if (!current) return current;
        const cells = {};
        Object.entries(current.cells).forEach(([key, cell]) => {
          const tables = tablesByCell.get(key) ?? cell.tables;
          cells[key] = { ...cell, tables, tableCount: tables.length };
        });
        return { ...current, cells };
      });
    } catch (_) {
      /* keep the cached tables; the next full load corrects them */
    }
  }, [floorId]);

  // 0 means "no desk change yet", so the initial render does not refetch.
  useEffect(() => {
    if (desksVersion > 0) refreshTables();
  }, [desksVersion, refreshTables]);

  // The sibling list is what tells us which storey is the lowest, and so which
  // one owns the entrance. Fall back to this floor alone if the list failed.
  const floorsForRules = useMemo(() => {
    if (!floor) return [];
    const list = siblings.length ? siblings : [{ id: floor.id, level: floor.level, code: floor.code }];
    return list.map((f) => (f.id === floor.id ? { ...f, ...floor } : f));
  }, [siblings, floor]);

  const isLowestFloor = useMemo(() => {
    if (!floor || floorsForRules.length === 0) return false;
    return lowestFloorOf(floorsForRules)?.id === floor.id;
  }, [floor, floorsForRules]);

  const buildingType = useMemo(
    () => buildingTypeOf(building ?? floor?.building),
    [building, floor],
  );

  // Rooms only. Circulation is painted as a role, not chosen as a purpose.
  const pickableSpaceTypes = useMemo(
    () => roomSpaceTypes(spaceTypes, buildingType?.code ?? null),
    [spaceTypes, buildingType],
  );

  // ------------------------------------------------------- validation
  const validation = useMemo(
    () => (floor ? validateFloor(floor, floorsForRules, buildingCells) : null),
    [floor, floorsForRules, buildingCells],
  );
  const census = useMemo(() => (floor ? summariseFloor(floor) : null), [floor]);

  const selectedCell = useMemo(
    () => (floor && selected ? floor.cells[cellKey(selected[0], selected[1])] ?? null : null),
    [floor, selected],
  );

  /**
   * A room carrying a space type the picker no longer offers — in practice one
   * saved as circulation before that became a role rather than a purpose. It is
   * kept in the list so the value stays visible and can be corrected; dropping
   * it would blank the field and hide the mismatch instead of surfacing it.
   */
  const strandedSpaceType = useMemo(() => {
    const id = selectedCell?.spaceTypeId;
    if (!id) return null;
    if (pickableSpaceTypes.some((s) => s.space_type_id === id)) return null;
    return spaceTypes.find((s) => s.space_type_id === id) ?? null;
  }, [selectedCell, pickableSpaceTypes, spaceTypes]);

  /**
   * Which furniture layout this cell would draw, and why. Repeated
   * client-side (resolveTemplateForCell, module-level) because the inspector
   * must show a verdict for a cell the analyst has just changed but not yet
   * saved, and an unsaved cell has nothing to ask the server about.
   */
  const resolvedTemplate = useMemo(
    () => resolveTemplateForCell(selectedCell, roomTemplates),
    [selectedCell, roomTemplates],
  );

  /**
   * A pin that no longer fits — pinned when the room was larger, or saved
   * before the fit was enforced. A pin wins over size in resolveTemplateForCell,
   * so this would otherwise sit there silently claiming a layout the room has no
   * space to draw.
   */
  const pinnedTooLarge = useMemo(
    () =>
      resolvedTemplate.match === "pinned" &&
      !templateFitsCell(resolvedTemplate.template, selectedCell),
    [resolvedTemplate, selectedCell],
  );

  // Plain language, not the raw token — an analyst reads this, not the API.
  const MATCH_REASON = {
    pinned: "pinned to this room",
    exact: "drawn for this size",
    fits: "the largest layout that fits this room",
    default: "the default for this space type",
    none: "No layout for this space type — the room draws bare",
  };

  /**
   * A room that already has real desks with jobs on them, but was never given
   * a purpose (99 such rooms live). Offered only for the SELECTED room, and
   * only ever applied when the analyst clicks Accept — never a bulk write.
   */
  const spaceSuggestion = useMemo(() => {
    if (!selectedCell || selectedCell.cellType !== "EMPTY" || selectedCell.spaceTypeId) return null;
    const tables = selectedCell.tables ?? [];
    if (!tables.length) return null;
    const hasJob = tables.some((t) => (t.tableJobs ?? []).length > 0);
    if (!hasJob) return null;
    const seatCount = realSeatsOf(selectedCell);
    const code = inferSpaceTypeCodeFromSeatCount(seatCount);
    if (!code) return null;
    const type = pickableSpaceTypes.find((s) => s.code === code);
    if (!type) return null;
    return { type, seatCount };
  }, [selectedCell, pickableSpaceTypes]);

  /**
   * The seat cap, checked as a TRANSITION exactly the way the server does
   * (checkSeatCapacityTransition) — a room already over its cap may keep
   * being edited and may shed seats, but may not gain seats. Only rooms
   * actually being edited this session are checked: an untouched room that
   * happens to already be over cap must load and stay editable, not get
   * blocked for a change nobody made.
   */
  const seatCapIssues = useMemo(() => {
    if (!floor) return [];
    const issues = [];
    Object.entries(floor.cells).forEach(([key, cell]) => {
      if (!dirty[cell.roomId]) return;
      if (cell.absorbedBy != null || cell.cellType !== "EMPTY") return;
      const currentCells = (cell.rowSpan || 1) * (cell.colSpan || 1);
      const currentSeats = realSeatsOf(cell);
      const resolved = resolveTemplateForCell(cell, roomTemplates);
      const nextSeats = resolved.template ? deskSeatSlots(resolved.template).reduce((n, s) => n + s, 0) : 0;
      const verdict = checkSeatCapacityTransition(currentSeats, currentCells, nextSeats, currentCells);
      if (!verdict.allowed) {
        const [r, c] = key.split(",").map(Number);
        issues.push({
          severity: "error",
          code: "seat-cap-exceeded",
          title: `${cell.name}: this label would exceed the seat cap`,
          detail: verdict.message,
          cells: [[r, c]],
        });
      }
    });
    return issues;
  }, [floor, roomTemplates, dirty]);

  const allIssues = useMemo(
    () => [...(validation?.issues ?? []), ...seatCapIssues],
    [validation, seatCapIssues],
  );
  const blocked = !canSave(allIssues);
  const dirtyCount = Object.keys(dirty).length;

  const badCells = useMemo(() => {
    const set = new Set();
    allIssues
      .filter((i) => i.severity === "error")
      .forEach((i) => i.cells.forEach(([r, c]) => set.add(cellKey(r, c))));
    return set;
  }, [allIssues]);

  // ------------------------------------------------------------ edit
  const snapshot = useCallback(() => {
    if (!floor) return;
    history.current.push({ cells: JSON.parse(JSON.stringify(floor.cells)), dirty: { ...dirty } });
    if (history.current.length > 50) history.current.shift();
  }, [floor, dirty]);

  const undo = useCallback(() => {
    const prev = history.current.pop();
    if (!prev) return;
    setFloor((f) => (f ? { ...f, cells: prev.cells } : f));
    setDirty(prev.dirty);
  }, []);

  const applyToCell = useCallback((row, column, change) => {
    setFloor((f) => {
      if (!f) return f;
      const key = cellKey(row, column);
      const cell = f.cells[key];
      if (!cell) return f;
      const next = { ...cell, ...change };
      // A manual pick of the space type is a human decision, not a guess —
      // clear any previously-accepted "Custom" suggestion unless this very
      // change is the one setting it (accept-suggestion passes both keys).
      if ("spaceTypeId" in change && !("pendingCustom" in change)) {
        next.pendingCustom = false;
      }
      // Only a room has a purpose — and only a room can pin a furniture layout.
      if (next.cellType !== "EMPTY") {
        next.spaceTypeId = null;
        next.roomTemplateId = null;
        next.pendingCustom = false;
      }
      if (
        next.cellType === cell.cellType &&
        next.spaceTypeId === cell.spaceTypeId &&
        next.roomTemplateId === cell.roomTemplateId &&
        next.pendingCustom === cell.pendingCustom
      )
        return f;
      setDirty((d) => ({ ...d, [cell.roomId]: true }));
      return { ...f, cells: { ...f.cells, [key]: next } };
    });
  }, []);

  const paint = useCallback(
    (row, column) => {
      const cell = floor?.cells?.[cellKey(row, column)];
      if (!cell || cell.cellType === tool) return;
      if (cell.absorbedBy != null) {
        toast.error("This cell is part of a larger room. Select that room and split it first.");
        return;
      }
      if (isLockedRole(cell.cellType)) {
        toast.error(
          `${roleName(cell.cellType)} cores are set on the building, not per floor. Change them in Edit Building.`,
        );
        return;
      }
      if (tool === "ENTRANCE" && !isLowestFloor) {
        toast.error("Only the lowest floor has a way in from outside. Use a lobby here instead.");
        return;
      }
      // Turning a furnished room into circulation would orphan its desks.
      if (tool !== "EMPTY" && cell.tableCount > 0) {
        toast.error(
          `${cell.name} has ${cell.tableCount} desk${cell.tableCount === 1 ? "" : "s"}. Remove them before changing it to ${roleName(tool).toLowerCase()}.`,
        );
        return;
      }
      applyToCell(row, column, { cellType: tool });
    },
    [floor, tool, isLowestFloor, applyToCell],
  );

  useEffect(() => {
    const onUp = () => {
      painting.current = false;
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  useEffect(() => {
    if (readOnly) return undefined;
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (/^(INPUT|SELECT|TEXTAREA)$/.test(tag || "")) return;
      const role = ROLES.find((r) => r.key === e.key);
      if (role && !(role.id === "ENTRANCE" && !isLowestFloor)) setTool(role.id);
      if (e.key === "Escape") {
        setSelected(null);
        setMarquee(null);
        setMerging(false);
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, readOnly, isLowestFloor]);

  // ----------------------------------------------------- merge/split
  // Merging writes straight through: it moves rows on the server (the absorbed
  // cells keep their ids and point at the owner), so it is not something to
  // hold in local state alongside unsaved paint.
  const marqueeRect = useMemo(() => {
    if (!marquee) return null;
    const [r1, c1] = marquee.from;
    const [r2, c2] = marquee.to;
    return {
      row: Math.min(r1, r2),
      column: Math.min(c1, c2),
      row_span: Math.abs(r1 - r2) + 1,
      col_span: Math.abs(c1 - c2) + 1,
    };
  }, [marquee]);

  const marqueeCells = useMemo(() => {
    if (!marqueeRect) return new Set();
    const s = new Set();
    for (let r = marqueeRect.row; r < marqueeRect.row + marqueeRect.row_span; r++) {
      for (let c = marqueeRect.column; c < marqueeRect.column + marqueeRect.col_span; c++) {
        s.add(cellKey(r, c));
      }
    }
    return s;
  }, [marqueeRect]);

  /**
   * Why this rectangle cannot become one room, or null if it can.
   *
   * The server refuses these too — it never destroys a row it was not asked to
   * — but saying so before the round-trip is the difference between a hint and
   * an error message.
   */
  const mergeObjection = useCallback(
    (rect) => {
      if (!rect || !floor) return null;
      const swallowed = [];
      for (let r = rect.row; r < rect.row + rect.row_span; r++) {
        for (let c = rect.column; c < rect.column + rect.col_span; c++) {
          if (r === rect.row && c === rect.column) continue;
          swallowed.push([r, c, floor.cells[cellKey(r, c)]]);
        }
      }
      const core = swallowed.find(([, , x]) => x && isLockedRole(x.cellType));
      if (core) {
        return `R${core[0]}·C${core[1]} is a ${roleName(core[2].cellType).toLowerCase()} core, which is fixed on the building.`;
      }
      const desks = swallowed.find(([, , x]) => x && x.tableCount > 0);
      if (desks) {
        return `R${desks[0]}·C${desks[1]} holds ${desks[2].tableCount} desk${desks[2].tableCount === 1 ? "" : "s"}. Remove them first, or they would be left inside a room that no longer exists.`;
      }
      const walk = swallowed.find(([, , x]) => x && WALKABLE_ROLES.has(x.cellType));
      if (walk) {
        return `R${walk[0]}·C${walk[1]} is ${roleName(walk[2].cellType).toLowerCase()}. Swallowing it into a room takes it out of the circulation people walk along.`;
      }
      const taken = swallowed.find(([, , x]) => x && x.absorbedBy != null);
      if (taken) {
        return `R${taken[0]}·C${taken[1]} already belongs to another room. Split that one apart first.`;
      }
      return null;
    },
    [floor],
  );

  const runMerge = async (rect) => {
    if (!rect || !floor) return;
    const objection = rect.row_span * rect.col_span > 1 ? mergeObjection(rect) : null;
    if (objection) {
      toast.error(objection);
      return;
    }
    setBusy(true);
    try {
      await mergeFloorCells(floor.id, rect);
      const n = rect.row_span * rect.col_span;
      toast.success(
        n === 1
          ? "Room split back into single cells."
          : `Merged ${n} cells into one room.`,
      );
      setMarquee(null);
      setMerging(false);
      setSelected(null);
      await reloadAndNotify("merge");
    } catch (e) {
      // The server refuses rather than destroys — a merge over a core or over
      // a room holding desks comes back with the reason.
      toast.error(e?.message || "Those cells could not be merged.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Shrinking a merged room is the one action in this designer that can
   * REDUCE its seat cap while its real tables stay exactly as they are — the
   * seat-cap transition rule (checkSeatCapacityTransition) is what decides
   * whether that is still allowed, the same way the server would.
   */
  const splitObjection = useMemo(() => {
    if (!selectedCell) return null;
    const currentCells = (selectedCell.rowSpan || 1) * (selectedCell.colSpan || 1);
    if (currentCells <= 1) return null;
    const seats = realSeatsOf(selectedCell);
    const verdict = checkSeatCapacityTransition(seats, currentCells, seats, 1);
    if (verdict.allowed) return null;

    // The shared verdict message is phrased for a write ("This room has N
    // cells…"), where N is the state AFTER it. Read against a split that has not
    // happened, it describes the room as already being something it is not. Say
    // what splitting would do instead.
    return (
      `Splitting would leave all ${seats} seat${seats === 1 ? "" : "s"} in a single cell, ` +
      `over the ${MAX_SEATS_PER_CELL}-per-cell limit. ` +
      `Remove desks, then split.`
    );
  }, [selectedCell]);

  const splitSelected = async () => {
    if (!selected || !floor) return;
    if (splitObjection) {
      toast.error(splitObjection);
      return;
    }
    // Spans of 1 un-merge: the anchor shrinks and every cell it had absorbed is
    // released, with the same ids it always had.
    await runMerge({ row: selected[0], column: selected[1], row_span: 1, col_span: 1 });
  };

  // ------------------------------------------------------------ save
  const handleSave = async () => {
    if (blocked) {
      toast.error("Fix the layout problems before saving.");
      return;
    }
    const edits = Object.values(floor.cells).filter((c) => dirty[c.roomId]);
    if (!edits.length) {
      toast("Nothing to save yet.");
      return;
    }
    setSaving(true);
    const layoutResults = await Promise.allSettled(
      edits.map((c) =>
        patchRoomLayout(c.roomId, {
          cellType: c.cellType,
          space_type_id: c.spaceTypeId ?? null,
          room_template_id: c.roomTemplateId ?? null,
        }),
      ),
    );
    const layoutFailed = layoutResults.filter((r) => r.status === "rejected");
    if (layoutFailed.length) {
      setSaving(false);
      toast.error(
        `${layoutFailed.length} of ${edits.length} changes could not be saved. Nothing else was lost — try again.`,
      );
      return;
    }

    // Furnish every room whose label just changed — creating or removing REAL
    // desks to match its resolved layout — and rename the ones that accepted
    // a seat-count suggestion so it is clear the label was inferred, not
    // chosen. Both only ever run for edits the write above actually saved.
    const rooms = edits.filter((c) => c.cellType === "EMPTY");
    const furnishResults = await Promise.allSettled(
      rooms.map((c) => reconcileRoomFurniture(c, roomTemplates)),
    );
    const renameResults = await Promise.allSettled(
      rooms
        .filter((c) => c.pendingCustom)
        .map((c) => patchRoom(c.roomId, { roomCode: c.roomCode, name: withCustomTag(c.name) })),
    );
    const followUpFailed = [...furnishResults, ...renameResults].filter((r) => r.status === "rejected");
    setSaving(false);

    if (followUpFailed.length) {
      toast.error(
        `Layout saved, but ${followUpFailed.length} follow-up change${followUpFailed.length === 1 ? "" : "s"} — furnishing or labelling — failed. Reopen the room and try again.`,
      );
    } else {
      toast.success(`Saved ${edits.length} cell${edits.length === 1 ? "" : "s"}.`);
    }
    await reloadAndNotify("save");
  };

  // ---------------------------------------------------------- render
  if (status === "loading") {
    return (
      <div className="fld-page fld-embed">
        <p className="fld-muted" role="status">
          Loading this floor's layout…
        </p>
      </div>
    );
  }
  if (status === "empty") {
    return (
      <div className="fld-page fld-embed">
        <p className="fld-muted">
          Save the floor first — its cells are created with it, and the plan opens here straight
          after.
        </p>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="fld-page fld-embed">
        <div className="fld-error" role="alert">
          <span>{error}</span>
          <button type="button" className="fld-btn" onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (!floor || !floor.rows || !floor.columns) {
    return (
      <div className="fld-page fld-embed">
        <p className="fld-muted">
          This floor's building has no grid yet. Set its rows and columns in Edit Building, then
          come back.
        </p>
      </div>
    );
  }

  const marqueeObjection =
    marqueeRect && marqueeRect.row_span * marqueeRect.col_span > 1
      ? mergeObjection(marqueeRect)
      : null;
  const selectedIsMerged =
    selectedCell && (selectedCell.rowSpan > 1 || selectedCell.colSpan > 1);

  return (
    <div className="fld-page fld-embed">
      <header className="fld-embed-head">
        <div className="fld-brand">
          <h2>Design layout</h2>
          <p>
            {floor.name}
            {floor.building?.name ? ` · ${floor.building.name}` : ""}
            {` · ${floor.rows}×${floor.columns} grid`}
            {isLowestFloor ? " · lowest floor" : ""}
          </p>
        </div>
        <div className="fld-spacer" />
        {!readOnly && dirtyCount > 0 && (
          <span className="fld-dirty" aria-live="polite">
            {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
          </span>
        )}
        {!readOnly && (
          <>
            <button type="button" className="fld-btn" onClick={undo} disabled={busy}>
              Undo
            </button>
            <button
              type="button"
              className="fld-btn fld-primary"
              onClick={handleSave}
              disabled={saving || busy || blocked || dirtyCount === 0}
              aria-describedby={blocked ? "fld-save-block" : undefined}
            >
              {saving ? "Saving…" : "Save layout"}
            </button>
          </>
        )}
      </header>

      {!readOnly && blocked && (
        <p className="fld-blocked" id="fld-save-block" role="alert">
          <b>Saving is off until the layout works.</b> Either part of this floor cannot be reached,
          or a pending change would leave a room over its seat cap — see the checks below.
        </p>
      )}
      {readOnly && (
        <p className="fld-readonly" role="status">
          Read-only view. Open <b>Edit Floor</b> to change the plan.
        </p>
      )}

      <div className={`fld-main${readOnly ? " fld-main-view" : ""}`}>
        {!readOnly && (
          <aside className="fld-panel">
            <div className="fld-group">
              <h3 className="fld-label">Paint with</h3>
              <div className="fld-tools" role="group" aria-label="Cell role to paint">
                {ROLES.map((r) => {
                  const unavailable = r.id === "ENTRANCE" && !isLowestFloor;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className={`fld-tool${tool === r.id ? " is-active" : ""}`}
                      aria-pressed={tool === r.id}
                      disabled={unavailable}
                      title={unavailable ? "Only the lowest floor has an entrance" : undefined}
                      onClick={() => setTool(r.id)}
                    >
                      <span className={`fld-sw fld-sw-${r.id.toLowerCase()}`} aria-hidden="true" />
                      <span className="fld-tool-name">{r.name}</span>
                      <span className="fld-key" aria-hidden="true">
                        {r.key}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="fld-hint">{(ROLE_BY_ID[tool] ?? ROLE_BY_ID.EMPTY).hint}</p>
              <p className="fld-hint">
                Click a cell to paint it, or drag across several. Keys 1–5 switch tool, Ctrl+Z
                undoes.
              </p>
            </div>

            <div className="fld-group">
              <h3 className="fld-label">Larger rooms</h3>
              <button
                type="button"
                className={`fld-btn${merging ? " fld-primary" : ""}`}
                aria-pressed={merging}
                disabled={busy}
                onClick={() => {
                  setMerging((m) => !m);
                  setMarquee(null);
                }}
              >
                {merging ? "Cancel merge" : "Merge cells into one room"}
              </button>
              {merging && (
                <p className="fld-hint" aria-live="polite">
                  Drag a rectangle on the plan, then confirm. Cores and cells holding desks cannot
                  be swallowed.
                </p>
              )}
              {marqueeRect && merging && (
                <div className="fld-confirm" role="status">
                  <span>
                    Merge R{marqueeRect.row}·C{marqueeRect.column} → R
                    {marqueeRect.row + marqueeRect.row_span - 1}·C
                    {marqueeRect.column + marqueeRect.col_span - 1} (
                    {marqueeRect.row_span * marqueeRect.col_span} cells) into one room?
                  </span>
                  {marqueeObjection && (
                    <span className="fld-objection">
                      <b>Not this rectangle.</b> {marqueeObjection}
                    </span>
                  )}
                  <div className="fld-confirm-actions">
                    <button
                      type="button"
                      className="fld-btn fld-primary"
                      disabled={
                        busy ||
                        Boolean(marqueeObjection) ||
                        marqueeRect.row_span * marqueeRect.col_span < 2
                      }
                      onClick={() => runMerge(marqueeRect)}
                    >
                      {busy ? "Merging…" : "Merge"}
                    </button>
                    <button type="button" className="fld-btn" onClick={() => setMarquee(null)}>
                      Clear
                    </button>
                  </div>
                </div>
              )}
              <p className="fld-hint">
                Merging saves immediately, and it is reversible: the cells a room swallows keep
                their records, so splitting restores them exactly.
              </p>
            </div>

            <div className="fld-group">
              <h3 className="fld-label">Set on the building</h3>
              <ul className="fld-locked-list">
                {LOCKED_ROLES.map((r) => (
                  <li key={r.id} className="fld-locked">
                    <span className={`fld-sw fld-sw-${r.id.toLowerCase()}`} aria-hidden="true" />
                    <span className="fld-tool-name">{r.name}</span>
                    <span className="fld-lock" aria-label="locked">
                      Locked
                    </span>
                  </li>
                ))}
              </ul>
              <p className="fld-hint">
                Cores are placed once in Edit Building and stamped onto every floor, so they line up
                from storey to storey. This plan's job is to put a corridor beside them.
              </p>
            </div>
          </aside>
        )}

        <section className="fld-canvas">
          <div className="fld-canvas-head">
            <h3>{floor.name}</h3>
            <span className="fld-hint">
              Row and column numbers match the coordinates stored against each room.
            </span>
          </div>

          <div className="fld-plan-scroll">
            <div
              className="fld-plan"
              role="grid"
              aria-label={`${floor.name} layout grid`}
              style={{
                gridTemplateColumns: `32px repeat(${floor.columns}, 64px)`,
                gridTemplateRows: `32px repeat(${floor.rows}, 64px)`,
              }}
            >
              <span className="fld-ruler" style={{ gridArea: "1 / 1" }} aria-hidden="true" />
              {Array.from({ length: floor.columns }, (_, i) => (
                <span
                  key={`c${i}`}
                  className="fld-ruler"
                  style={{ gridArea: `1 / ${i + 2}` }}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
              ))}
              {Array.from({ length: floor.rows }, (_, i) => (
                <span
                  key={`r${i}`}
                  className="fld-ruler"
                  style={{ gridArea: `${i + 2} / 1` }}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
              ))}

              {Array.from({ length: floor.rows }, (_, ri) =>
                Array.from({ length: floor.columns }, (_, ci) => {
                  const row = ri + 1;
                  const column = ci + 1;
                  const key = cellKey(row, column);
                  const cell = floor.cells[key];
                  if (!cell) {
                    return (
                      <span
                        key={key}
                        className="fld-cell fld-missing"
                        style={{ gridArea: `${row + 1} / ${column + 1}` }}
                        title="No room record for this cell"
                      >
                        —
                      </span>
                    );
                  }
                  // An absorbed cell is drawn by its owner, not by itself.
                  if (cell.absorbedBy != null) return null;

                  const bad = badCells.has(key);
                  const focused = focusCells.some(([r, c]) => r === row && c === column);
                  const isSel = selected && selected[0] === row && selected[1] === column;
                  const locked = isLockedRole(cell.cellType);
                  const inMarquee = marqueeCells.has(key);
                  const spaceName = pickableSpaceTypes.find(
                    (s) => s.space_type_id === cell.spaceTypeId,
                  )?.name;
                  const spans = cell.rowSpan > 1 || cell.colSpan > 1;

                  return (
                    <button
                      key={key}
                      type="button"
                      role="gridcell"
                      style={{
                        gridArea: `${row + 1} / ${column + 1} / span ${cell.rowSpan} / span ${cell.colSpan}`,
                      }}
                      className={
                        `fld-cell fld-${cell.cellType.toLowerCase()}` +
                        (isSel ? " is-selected" : "") +
                        (bad ? " is-bad" : "") +
                        (focused ? " is-focus" : "") +
                        (locked ? " is-locked" : "") +
                        (spans ? " is-merged" : "") +
                        (inMarquee ? " is-marquee" : "")
                      }
                      aria-label={
                        `${roleName(cell.cellType)} at row ${row} column ${column}` +
                        (spans ? `, ${cell.rowSpan} by ${cell.colSpan} cells` : "") +
                        (spaceName ? `, ${spaceName}` : "") +
                        (locked ? ", locked; set on the building" : "") +
                        (bad ? ", cannot be reached" : "")
                      }
                      onMouseDown={() => {
                        setSelected([row, column]);
                        setFocusCells([]);
                        if (readOnly) return;
                        if (merging) {
                          painting.current = true;
                          setMarquee({ from: [row, column], to: [row, column] });
                          return;
                        }
                        snapshot();
                        painting.current = true;
                        paint(row, column);
                      }}
                      onMouseEnter={() => {
                        if (readOnly || !painting.current) return;
                        if (merging) setMarquee((m) => (m ? { ...m, to: [row, column] } : m));
                        else paint(row, column);
                      }}
                    >
                      {bad && <span className="fld-flag" aria-hidden="true" />}
                      {locked && <span className="fld-lock-pip" aria-hidden="true" title="Locked" />}
                      <span className="fld-ct">
                        {cell.cellType === "EMPTY" ? "ROOM" : cell.cellType}
                      </span>
                      {spaceName && (
                        <span className="fld-st">
                          {spaceName}
                          {isCustomLabelled(cell.name) ? " · Custom" : ""}
                        </span>
                      )}
                      {spans && (
                        <span className="fld-span" aria-hidden="true">
                          {cell.rowSpan}×{cell.colSpan}
                        </span>
                      )}
                      {cell.tableCount > 0 && (
                        <span className="fld-desks" aria-hidden="true">
                          {cell.tableCount} desk{cell.tableCount === 1 ? "" : "s"}
                        </span>
                      )}
                    </button>
                  );
                }),
              )}
            </div>
          </div>
        </section>

        <aside className="fld-panel">
          <div className="fld-group">
            <h3 className="fld-label">This floor</h3>
            <div className="fld-stats">
              <div className="fld-stat">
                <span className="fld-n">{census.rooms.length}</span>
                <span className="fld-l">Rooms</span>
              </div>
              <div className="fld-stat">
                <span className="fld-n">{census.circulation.length}</span>
                <span className="fld-l">Circulation</span>
              </div>
              <div className="fld-stat">
                <span className="fld-n">{census.shafts.length}</span>
                <span className="fld-l">Cores</span>
              </div>
              <div className="fld-stat">
                <span className="fld-n">{census.mergedRooms.length}</span>
                <span className="fld-l">Merged</span>
              </div>
            </div>
          </div>

          <div className="fld-group">
            <h3 className="fld-label">Selected space</h3>
            {!selectedCell ? (
              <p className="fld-muted">
                Select a cell on the plan to {readOnly ? "read its details" : "set what it is used for"}.
              </p>
            ) : (
              <>
                <dl className="fld-kv">
                  <div>
                    <dt>Room</dt>
                    <dd>{selectedCell.name}</dd>
                  </div>
                  <div>
                    <dt>Grid cell</dt>
                    <dd className="fld-mono">
                      R{selected[0]} · C{selected[1]}
                    </dd>
                  </div>
                  <div>
                    <dt>Role</dt>
                    <dd>{roleName(selectedCell.cellType)}</dd>
                  </div>
                  <div>
                    <dt>Size</dt>
                    <dd className="fld-mono">
                      {selectedCell.rowSpan}×{selectedCell.colSpan}
                    </dd>
                  </div>
                  {selectedCell.cellType === "EMPTY" && (
                    <div>
                      <dt>Label source</dt>
                      <dd>
                        {!selectedCell.spaceTypeId
                          ? "Not set"
                          : isCustomLabelled(selectedCell.name)
                            ? "Custom — inferred from existing desks"
                            : "Chosen"}
                      </dd>
                    </div>
                  )}
                </dl>
                {!readOnly && selectedIsMerged && (
                  <div className="fld-field">
                    {/* The reason hangs off a wrapper, not the button: a truly
                        `disabled` control fires no hover events, so a tooltip on
                        it would never appear. The button also stays focusable
                        via aria-disabled, so the reason is reachable by keyboard
                        — and clicking still reports it, since splitSelected
                        refuses and toasts the same message. */}
                    <span
                      className="fld-tipwrap"
                      data-blocked={splitObjection ? "true" : undefined}
                    >
                      <button
                        type="button"
                        className={`fld-btn${splitObjection ? " is-blocked" : ""}`}
                        disabled={busy}
                        aria-disabled={Boolean(splitObjection)}
                        aria-describedby={splitObjection ? "fld-split-block" : undefined}
                        onClick={splitSelected}
                      >
                        {busy
                          ? "Splitting…"
                          : `Split back into ${selectedCell.rowSpan * selectedCell.colSpan} cells`}
                      </button>
                      {splitObjection && (
                        <span className="fld-tip" id="fld-split-block" role="tooltip">
                          <b>Not yet.</b> {splitObjection}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {selectedCell.cellType === "EMPTY" ? (
                  <div className="fld-field">
                    <label className="fld-label" htmlFor="fld-space">
                      Used as
                    </label>
                    <select
                      id="fld-space"
                      value={selectedCell.spaceTypeId ?? ""}
                      disabled={readOnly}
                      onChange={(e) => {
                        snapshot();
                        applyToCell(selected[0], selected[1], {
                          spaceTypeId: e.target.value ? Number(e.target.value) : null,
                        });
                      }}
                    >
                      <option value="">Not set</option>
                      {pickableSpaceTypes.map((s) => (
                        <option key={s.space_type_id} value={s.space_type_id}>
                          {s.name}
                        </option>
                      ))}
                      {strandedSpaceType && (
                        <option
                          key={strandedSpaceType.space_type_id}
                          value={strandedSpaceType.space_type_id}
                        >
                          {strandedSpaceType.name} (not valid for a room)
                        </option>
                      )}
                    </select>
                    {strandedSpaceType ?
                      <p className="fld-objection" role="alert">
                        <b>This room is used as {strandedSpaceType.name}.</b>{" "}
                        Circulation is a cell's role, not a room's purpose —
                        paint the cell{" "}
                        {strandedSpaceType.is_circulation ?
                          "with the matching palette role"
                        : "with the right role"}{" "}
                        instead, or pick a room space here.
                      </p>
                    : buildingType ?
                      <p className="fld-hint">
                        Filtered to <b>{buildingType.name}</b> spaces, plus the
                        ones every building has. Corridors and lobbies are
                        painted as a role, not chosen here.
                      </p>
                    : <p className="fld-note" role="status">
                        This building has no building type, so every space in
                        the catalogue is listed. Set one in Edit Building to
                        narrow this to the spaces that kind of building
                        actually has.
                      </p>
                    }
                  </div>
                ) : (
                  <p className="fld-hint">{(ROLE_BY_ID[selectedCell.cellType] ?? ROLE_BY_ID.EMPTY).hint}</p>
                )}
                {!readOnly && spaceSuggestion && (
                  <div className="fld-confirm" role="status">
                    <span>
                      <b>
                        {spaceSuggestion.seatCount} seat{spaceSuggestion.seatCount === 1 ? "" : "s"} already
                        assigned here, but no purpose is set.
                      </b>{" "}
                      Suggested: <b>{spaceSuggestion.type.name}</b> — marked{" "}
                      <b>Custom</b>, so it stays clear this is a guess from the existing desks, not
                      something a person chose.
                    </span>
                    <div className="fld-confirm-actions">
                      <button
                        type="button"
                        className="fld-btn fld-primary"
                        onClick={() => {
                          snapshot();
                          applyToCell(selected[0], selected[1], {
                            spaceTypeId: spaceSuggestion.type.space_type_id,
                            pendingCustom: true,
                          });
                        }}
                      >
                        Accept — label as {spaceSuggestion.type.name}
                      </button>
                    </div>
                  </div>
                )}
                {selectedCell.cellType === "EMPTY" && (
                  <div className="fld-divider">
                    <h4 className="fld-label">Furniture layout</h4>
                    {!selectedCell.spaceTypeId ? (
                      <p className="fld-hint">
                        Choose what this room is for, above, and a layout is chosen for it.
                      </p>
                    ) : (
                      <>
                        <p
                          id={`fld-tpl-reason-${selectedCell.roomId}`}
                          className="fld-hint fld-tpl-reason"
                        >
                          {resolvedTemplate.template ? (
                            <>
                              <strong>{resolvedTemplate.template.name}</strong>
                              {" — "}
                              {MATCH_REASON[resolvedTemplate.match]}
                            </>
                          ) : (
                            // No name to lead with, so the reason stands alone —
                            // pairing it with a "No layout" heading read as
                            // "No layout — no layout — this room draws bare".
                            <strong>{MATCH_REASON.none}</strong>
                          )}
                          {resolvedTemplate.template && (
                            <>
                              {" · "}
                              {resolvedTemplate.template.item?.length ?? 0} item
                              {(resolvedTemplate.template.item?.length ?? 0) === 1 ? "" : "s"}
                              {" · "}
                              {(resolvedTemplate.template.item ?? []).reduce(
                                (n, i) => n + (i.seats ?? 0),
                                0,
                              )}{" "}
                              seat
                              {(resolvedTemplate.template.item ?? []).reduce(
                                (n, i) => n + (i.seats ?? 0),
                                0,
                              ) === 1
                                ? ""
                                : "s"}
                            </>
                          )}
                        </p>
                        {pinnedTooLarge && (
                          <p className="fld-objection" role="alert">
                            <b>This layout does not fit.</b>{" "}
                            {resolvedTemplate.template.name} needs a{" "}
                            {resolvedTemplate.template.row_span}×
                            {resolvedTemplate.template.col_span} room and this
                            one is {selectedCell.rowSpan}×{selectedCell.colSpan}.
                            Merge cells to that size, or set the layout back to
                            Automatic.
                          </p>
                        )}
                        <div className="fld-field">
                          <label className="fld-label" htmlFor="fld-template">
                            Layout
                          </label>
                          <select
                            id="fld-template"
                            aria-describedby={`fld-tpl-reason-${selectedCell.roomId}`}
                            value={selectedCell.roomTemplateId ?? ""}
                            disabled={readOnly}
                            onChange={(e) => {
                              const id = e.target.value ? Number(e.target.value) : null;
                              const template =
                                id == null
                                  ? null
                                  : roomTemplates.find((t) => t.room_template_id === id);

                              // Refuse a layout bigger than the room rather than
                              // pin one that cannot be drawn. The select is
                              // controlled, so returning here snaps it back to
                              // what the room actually has.
                              if (template && !templateFitsCell(template, selectedCell)) {
                                toast.error(
                                  `${template.name} needs a ${template.row_span}×${template.col_span} room. This one is ${selectedCell.rowSpan}×${selectedCell.colSpan} — merge cells to that size first.`,
                                );
                                return;
                              }

                              snapshot();
                              applyToCell(selected[0], selected[1], {
                                roomTemplateId: id,
                              });
                            }}
                          >
                            <option value="">Automatic — chosen by size</option>
                            {roomTemplates
                              .filter((t) => t.space_type_id === selectedCell.spaceTypeId)
                              .map((t) => {
                                // Left selectable on purpose: picking one is how
                                // an analyst finds out what the room would need
                                // to be merged to.
                                const fits = templateFitsCell(t, selectedCell);
                                return (
                                  <option key={t.room_template_id} value={t.room_template_id}>
                                    {t.name} ({t.row_span}×{t.col_span})
                                    {fits ? "" : " — too large for this room"}
                                  </option>
                                );
                              })}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="fld-group">
            <h3 className="fld-label">Layout checks</h3>
            {allIssues.length === 0 ? (
              <div className="fld-check fld-ok">
                <span className="fld-dot" aria-hidden="true" />
                <span>
                  <b>This floor works</b>
                  <span className="fld-fix">
                    Every room and core can be reached, the circulation is continuous
                    {isLowestFloor ? ", and the way in is on the outside edge" : ""}.
                  </span>
                </span>
              </div>
            ) : (
              allIssues.map((issue, i) => (
                <div
                  key={`${issue.code}-${i}`}
                  className={`fld-check ${issue.severity === "error" ? "fld-bad" : "fld-warn"}`}
                >
                  <span className="fld-dot" aria-hidden="true" />
                  <span>
                    <b>
                      {issue.severity === "error" ? "Problem: " : "Worth fixing: "}
                      {issue.title}
                    </b>
                    <span className="fld-fix">{issue.detail}</span>
                    {issue.cells.length > 0 && (
                      <button
                        type="button"
                        className="fld-link"
                        onClick={() => setFocusCells(issue.cells)}
                      >
                        Show me on the plan
                      </button>
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
